import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Scale, Loader2, RefreshCw, Check, X, AlertTriangle, CalendarClock, Scissors, History,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminService } from '../../../services/adminService';
import ImpactPreviewDialog from './ImpactPreviewDialog';
import { fmtDate, hoursLabel, levelStyle, money } from '../../../lib/scheduleFormat';

// Hours the order book disagrees with. Every card is one night, applied or
// dismissed by a person — nothing here books, extends or shortens on its own.
const HoursProposalsCard = ({ kitchen, onChanged }) => {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyDate, setBusyDate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [impact, setImpact] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setView(await adminService.getHoursSuggestions(kitchen.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read the order book');
    } finally {
      setLoading(false);
    }
  }, [kitchen.id]);

  useEffect(() => { load(); }, [load]);

  const apply = async (date, force = false) => {
    setBusyDate(date);
    try {
      const result = await adminService.applyHoursSuggestion(kitchen.id, date, force);
      if (result.needs_confirmation) {
        setImpact({ date, impact: result.impact });
        return;
      }
      toast.success(`${fmtDate(date)} — hours now ${result.booking.start_time}–${result.booking.end_time}`);
      setImpact(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change those hours');
    } finally {
      setBusyDate(null);
    }
  };

  const dismiss = async (date) => {
    setBusyDate(date);
    try {
      await adminService.dismissHoursSuggestion(kitchen.id, date);
      toast.success('Left as it is — it comes back only if those orders change');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not dismiss that suggestion');
    } finally {
      setBusyDate(null);
    }
  };

  const correctSameDay = async () => {
    setBusy(true);
    try {
      const result = await adminService.serveNextDayBookings(kitchen.id);
      toast.success(`${result.corrected} booking(s) now serve the next day's deliveries`);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not re-file those bookings');
    } finally {
      setBusy(false);
    }
  };

  const proposals = view?.proposals || [];
  const warnings = view?.warnings || [];
  const sameDay = view?.same_day_bookings || [];
  const totals = view?.totals || {};

  return (
    <Card data-testid="hours-proposals-card">
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
            <Scale className="h-5 w-5 text-amber-600" /> Hours vs the order book
            {proposals.length > 0 && (
              <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-900"
                data-testid="proposals-count">
                {proposals.length} to decide
              </Badge>
            )}
          </CardTitle>
          <p className="text-sm text-gray-500">
            Each night judged on the orders it bakes for — the next day&apos;s. Suggestions only;
            nothing changes until you say so.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading || busy}
          data-testid="proposals-refresh">
          {loading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
          Re-check
        </Button>
      </CardHeader>

      <CardContent className="space-y-3">
        {sameDay.length > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
            data-testid="proposals-same-day">
            <p className="flex items-center gap-2 font-medium">
              <AlertTriangle className="h-4 w-4" />
              {sameDay.length} booking{sameDay.length === 1 ? '' : 's'} still recorded as serving their own day
            </p>
            <p className="mt-1 text-xs">
              You bake the night before, so those hours are credited to a day whose order book is
              still empty while the day you actually bake for reads as having no kitchen hours.
            </p>
            <p className="mt-1 text-xs text-red-800">
              {sameDay.slice(0, 6).map((row) => fmtDate(row.date)).join(' · ')}
              {sameDay.length > 6 ? ' …' : ''}
            </p>
            <Button size="sm" className="mt-2 bg-red-600 hover:bg-red-700" onClick={correctSameDay}
              disabled={busy} data-testid="proposals-fix-same-day">
              {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
              Re-file them onto the next day
            </Button>
          </div>
        )}

        {loading && <Loader2 className="h-5 w-5 animate-spin text-amber-600" />}

        {!loading && !view?.standards_configured && (
          <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600"
            data-testid="proposals-dormant">
            No labour standards are recorded, so no hours are suggested — a requirement worked out
            from no data would be a guess dressed as a warning. Set units per person-hour in
            Scheduling settings to turn these on. The warnings below need no arithmetic and still run.
          </p>
        )}

        {!loading && proposals.map((row) => (
          <div key={row.date} className="rounded-lg border border-amber-200 bg-amber-50/50 p-3"
            data-testid={`proposal-${row.date}`}>
            <p className="text-sm font-medium text-gray-900">{row.headline}</p>
            <p className="mt-0.5 text-sm text-gray-700" data-testid={`proposal-detail-${row.date}`}>
              {row.detail}
            </p>
            {row.trimmed && (
              <p className="mt-1 flex items-start gap-1.5 text-xs text-amber-900">
                <Scissors className="mt-0.5 h-3 w-3 shrink-0" /> {row.trim_note}
              </p>
            )}
            {row.volume_moved && (
              <p className="mt-1 flex items-start gap-1.5 text-xs text-amber-900"
                data-testid={`proposal-moved-${row.date}`}>
                <History className="mt-0.5 h-3 w-3 shrink-0" /> {row.volume_moved_note}
              </p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="bg-white text-xs">
                {row.current_window} → {row.proposed_window}
              </Badge>
              <Badge
                variant="outline"
                className={`text-xs ${row.direction === 'extend'
                  ? 'border-amber-300 bg-amber-100 text-amber-900'
                  : 'border-emerald-300 bg-emerald-50 text-emerald-800'}`}
                data-testid={`proposal-money-${row.date}`}
              >
                {row.direction === 'extend'
                  ? `+${hoursLabel(row.hours_delta)} · +${money(row.cost_delta)}`
                  : `saves ${hoursLabel(-row.hours_delta)} · ${money(-row.cost_delta)}`}
              </Badge>
              <span className="text-xs text-gray-600">
                {row.units} units · ≈{hoursLabel(row.required_hours)} of work · {row.crew}{' '}
                {row.crew === 1 ? 'baker' : 'bakers'}
              </span>
              <div className="ml-auto flex gap-2">
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => apply(row.date)} disabled={busyDate === row.date}
                  data-testid={`proposal-apply-${row.date}`}>
                  {busyDate === row.date ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />}
                  Apply
                </Button>
                <Button variant="outline" size="sm" onClick={() => dismiss(row.date)}
                  disabled={busyDate === row.date} data-testid={`proposal-dismiss-${row.date}`}>
                  <X className="mr-1 h-3.5 w-3.5" /> Dismiss
                </Button>
              </div>
            </div>
          </div>
        ))}

        {!loading && proposals.length === 0 && view?.standards_configured && (
          <p className="text-sm text-gray-600" data-testid="proposals-empty">
            Every night booked between {fmtDate(view.start)} and {fmtDate(view.end)} fits the orders
            it bakes for.
          </p>
        )}

        {!loading && warnings.length > 0 && (
          <div className="space-y-1.5 border-t border-gray-100 pt-3" data-testid="proposals-warnings">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Mismatches the pattern cannot fix on its own
            </p>
            {warnings.map((row) => (
              <p key={`${row.code}-${row.date}-${row.delivery_date || ''}`}
                className="flex items-start gap-2 text-xs text-gray-800"
                data-testid={`warning-${row.code}-${row.date}`}>
                <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${levelStyle(row.severity).dot}`} />
                <span>{row.message}</span>
              </p>
            ))}
          </div>
        )}

        {!loading && (view?.dismissed?.length || 0) > 0 && (
          <p className="flex items-center gap-2 text-xs text-gray-500" data-testid="proposals-dismissed">
            <CalendarClock className="h-3.5 w-3.5" />
            {view.dismissed.length} suggestion{view.dismissed.length === 1 ? '' : 's'} dismissed
            ({view.dismissed.map((row) => fmtDate(row.date)).join(' · ')}) — each comes back only if
            that date&apos;s orders change.
          </p>
        )}

        {!loading && (totals.proposals > 0 || totals.warnings > 0) && (
          <p className="text-[11px] text-gray-500">
            Seen here before the overnight top-up books a night the order book disagrees with.
          </p>
        )}
      </CardContent>

      {impact && (
        <ImpactPreviewDialog
          open
          impact={impact.impact}
          title="Shortening this night strands scheduled shifts"
          confirmLabel="Change the hours anyway"
          busy={busyDate === impact.date}
          onConfirm={() => apply(impact.date, true)}
          onCancel={() => setImpact(null)}
        />
      )}
    </Card>
  );
};

export default HoursProposalsCard;
